import React, { useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import StatusBadge, { STATUS_LABEL } from "@/components/app/StatusBadge";

// Ensure leaflet default icons resolve under webpack.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const STATUS_COLOR = {
  scheduled:  "#9CA3AF",
  picked_up:  "#F59E0B",
  in_transit: "#F97316",
  delivered:  "#059669",
  cancelled:  "#6B7280",
  rejected:   "#DC2626",
};

const pinIcon = (color, letter) => L.divIcon({
  className: "",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  popupAnchor: [0, -10],
  html: `<div style="
    width:26px;height:26px;border-radius:50%;background:${color};
    color:#fff;font-weight:800;font-size:11px;font-family:ui-sans-serif,system-ui;
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid #fff;">${letter}</div>`,
});

function FitAll({ bounds }) {
  const map = useMap();
  React.useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    if (bounds.length === 1) {
      map.setView(bounds[0], 12);
    } else {
      map.fitBounds(L.latLngBounds(bounds), { padding: [30, 30] });
    }
  }, [map, bounds]);
  return null;
}

/**
 * iOS Safari fix — Leaflet initializes with 0px height on mobile stacked layouts.
 * We force `invalidateSize()` after mount + window resize + orientation change
 * so tiles paint once the container has real dimensions.
 */
function InvalidateSizeOnMount() {
  const map = useMap();
  React.useEffect(() => {
    const tick = () => { try { map.invalidateSize(); } catch {} };
    // Run several times over ~2s to catch late layout settling on iOS Safari.
    const timers = [50, 200, 500, 1000, 2000].map((t) => setTimeout(tick, t));
    window.addEventListener("resize", tick);
    window.addEventListener("orientationchange", tick);
    document.addEventListener("visibilitychange", tick);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", tick);
      window.removeEventListener("orientationchange", tick);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [map]);
  return null;
}

/**
 * Multi-load map for Live Tracking. Renders one polyline per active load,
 * plus site pins (deduplicated) that are involved in any of them.
 * Only active loads (scheduled / picked_up / in_transit) are drawn.
 */
export default function TrackingMap({ loads, sites, trucks, drivers, onSelect }) {
  const activeLoads = useMemo(
    () => loads.filter((l) => ["scheduled", "picked_up", "in_transit"].includes(l.status)),
    [loads]
  );
  const sitesById = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);
  const trucksById = useMemo(() => Object.fromEntries(trucks.map((t) => [t.id, t])), [trucks]);
  const driversById = useMemo(() => Object.fromEntries(drivers.map((d) => [d.id, d])), [drivers]);

  const [selected, setSelected] = useState(null); // load id highlighted

  const routes = useMemo(() => {
    const out = [];
    for (const l of activeLoads) {
      const p = sitesById[l.pickup_site_id];
      const d = sitesById[l.dropoff_site_id];
      // Prefer the site's current coordinates; fall back to load-level overrides.
      const pLat = p?.lat ?? l.pickup_lat;
      const pLon = p?.lon ?? l.pickup_lon;
      const dLat = d?.lat ?? l.dropoff_lat;
      const dLon = d?.lon ?? l.dropoff_lon;
      if (pLat == null || pLon == null || dLat == null || dLon == null) continue;
      out.push({
        load: l,
        pickup:  { site: p, lat: pLat, lon: pLon },
        dropoff: { site: d, lat: dLat, lon: dLon },
      });
    }
    return out;
  }, [activeLoads, sitesById]);

  const bounds = useMemo(() => {
    const b = [];
    routes.forEach((r) => {
      b.push([r.pickup.lat, r.pickup.lon]);
      b.push([r.dropoff.lat, r.dropoff.lon]);
    });
    return b;
  }, [routes]);

  const skipped = activeLoads.length - routes.length;

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:h-[calc(100vh-14rem)]" data-testid="tracking-map">
      {/* Legend + load list */}
      <div className="lg:w-[320px] flex-shrink-0 bg-white border border-gray-200 flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="label-caps">Active Loads</div>
          <div className="font-display font-bold text-2xl tracking-tight">{routes.length}</div>
          {skipped > 0 && (
            <div className="text-[10px] uppercase tracking-widest text-amber-700 mt-1 font-mono">
              {skipped} skipped · missing coordinates
            </div>
          )}
        </div>
        <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-widest">
          {["scheduled", "picked_up", "in_transit"].map((s) => (
            <div key={s} className="flex items-center gap-1.5 text-gray-600">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOR[s] }} />
              {STATUS_LABEL[s]}
            </div>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {routes.length === 0 ? (
            <div className="p-6 text-xs text-gray-500 font-mono text-center">No active loads with coordinates.</div>
          ) : routes.map(({ load, pickup, dropoff }) => (
            <button
              key={load.id}
              onClick={() => { setSelected(load.id); onSelect?.(load); }}
              data-testid={`map-load-item-${load.bol_number}`}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                selected === load.id ? "bg-orange-50 border-l-2 border-l-orange-500" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[11px] text-gray-900">{load.bol_number}</span>
                <StatusBadge status={load.status} />
              </div>
              <div className="text-[11px] text-gray-600 flex items-center gap-1 truncate">
                <span className="truncate">{pickup.site?.name || "?"}</span>
                <span className="text-gray-400">→</span>
                <span className="truncate">{dropoff.site?.name || "?"}</span>
              </div>
              <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                {trucksById[load.truck_id]?.truck_number || "—"} · {driversById[load.driver_id]?.name || "—"} · {load.weight_tons}t
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 border border-gray-200 relative overflow-hidden h-[500px] lg:h-auto">
        {routes.length === 0 ? (
          <div className="h-full flex items-center justify-center bg-gray-50 text-sm text-gray-500 font-mono">
            No active loads to plot.
          </div>
        ) : (
          <MapContainer center={bounds[0]} zoom={11} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitAll bounds={bounds} />
            <InvalidateSizeOnMount />
            {routes.map(({ load, pickup, dropoff }) => {
              const isSel = selected === load.id;
              const color = STATUS_COLOR[load.status] || "#9CA3AF";
              return (
                <React.Fragment key={load.id}>
                  <Marker
                    position={[pickup.lat, pickup.lon]}
                    icon={pinIcon(color, "P")}
                    eventHandlers={{ click: () => setSelected(load.id) }}
                  >
                    <Popup>
                      <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500">Pickup · {load.bol_number}</div>
                      <div className="font-semibold">{pickup.site?.name}</div>
                      <div className="text-xs text-gray-600">{pickup.site?.address}</div>
                    </Popup>
                  </Marker>
                  <Marker
                    position={[dropoff.lat, dropoff.lon]}
                    icon={pinIcon(color, "D")}
                    eventHandlers={{ click: () => setSelected(load.id) }}
                  >
                    <Popup>
                      <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500">Dropoff · {load.bol_number}</div>
                      <div className="font-semibold">{dropoff.site?.name}</div>
                      <div className="text-xs text-gray-600">{dropoff.site?.address}</div>
                    </Popup>
                  </Marker>
                  <Polyline
                    positions={[[pickup.lat, pickup.lon], [dropoff.lat, dropoff.lon]]}
                    pathOptions={{
                      color,
                      weight: isSel ? 6 : 3,
                      opacity: isSel ? 0.95 : 0.65,
                      dashArray: load.status === "in_transit" ? null : "6 6",
                    }}
                    eventHandlers={{ click: () => setSelected(load.id) }}
                  />
                </React.Fragment>
              );
            })}
          </MapContainer>
        )}
      </div>
    </div>
  );
}
